import api from '@api';
import Card from '@components/common/Card';
import ArrowIcon from '@icons/arrow.svg?react';
import promptCategories from '@static/prompts.json';
import { useChatStore } from '@store/chat';
import * as utils from '@utils';
import type { MouseEvent } from 'react';
import { useMemo, useState } from 'react';
import type { ChatPromptRequest, ChatRequest } from 'types/schema';
import Styled from './PromptShowcase.styled';

interface Props {
  disabled?: boolean;
  // Use this property to avoid boiler plate code
  onSelectPrompt(message: ChatRequest & ChatPromptRequest): void;
}

function PromptShowcase(props: Props) {
  const selectedDataset = useChatStore((state) => state.selectedDataset);
  const randomIndex = Math.floor(Math.random() * promptCategories.length);
  const [categoryIndex, setCategoryIndex] = useState(randomIndex);
  const category = promptCategories[categoryIndex];

  const handleSelectPromptExample = (example: string) => {
    props.onSelectPrompt({
      prompt: example,
      ['message_type']: 'prompt'
    });

    // Track event
    api.analytics.selectedPromptExample({
      ['dataset_details']: selectedDataset.title,
      ['prompt_category']: category.title,
      ['prompt_template']: example
    });
  };

  const handleNavButtonClick =
    (direction: 'back' | 'forward') => (event: MouseEvent<HTMLSpanElement>) => {
      const parentElement = event.currentTarget.parentElement;

      window.requestAnimationFrame(() =>
        parentElement.scrollIntoView({
          behavior: 'instant',
          block: 'start'
        })
      );

      switch (direction) {
        case 'back':
          setCategoryIndex(
            categoryIndex > 0 ? categoryIndex - 1 : promptCategories.length - 1
          );
          break;
        case 'forward':
          setCategoryIndex(
            categoryIndex < promptCategories.length - 1 ? categoryIndex + 1 : 0
          );
          break;
      }

      api.analytics.changedPromptExampleCategory();
    };

  const randomExamples = useMemo(
    () => utils.getRandomExamples(category.examples),
    [category.examples]
  );

  return (
    <Styled.Wrapper>
      <Styled.Tip data-testid="tip-message">
        Not sure where to start?
        <br />
        Here are examples of what you can ask Agent Spark
      </Styled.Tip>
      <Styled.GridLayout>
        {randomExamples.map((example) => (
          <Card
            disabled={props.disabled}
            key={utils.generateKey(example)}
            onClick={() => handleSelectPromptExample(example)}>
            {example}
          </Card>
        ))}
      </Styled.GridLayout>
      <Styled.Nav>
        <Styled.NavButton
          direction="back"
          onClick={handleNavButtonClick('back')}>
          <ArrowIcon />
        </Styled.NavButton>
        <Styled.CategoryTitle>{category.title}</Styled.CategoryTitle>
        <Styled.NavButton
          direction="forward"
          onClick={handleNavButtonClick('forward')}>
          <ArrowIcon />
        </Styled.NavButton>
      </Styled.Nav>
    </Styled.Wrapper>
  );
}

export default PromptShowcase;
